import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import moment from 'moment';

import { customerInfo as customerInfoShape } from 'constants/propTypes';
import { Spinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { GET_LOYALTY_BUSINESS_ACCOUNT_BENEFITS, GET_LOYALTY_MEMBER_BENEFITS } from 'store/queries';

import {
  SectionContainer,
  SectionTableContainer,
  StyledTableCell,
  StyledTableHeader,
  StyledTableRow,
  BookingLink,
  AdditionalNotesIcon
} from '../ClubEurostarMember.styles';
import { Column, Row } from '../../../components/shared/Layout';
import Information from 'assets/rebrand/icons/information.svg';
import ReactTooltip from 'react-tooltip';

const DATE_FORMAT = 'DD-MMMM-YYYY';

const toolTipForComment = (popupId, comments) => {
  if (!comments) return null;
  return (
    <ReactTooltip className="tooltip" data-testid={popupId} id={popupId} place="bottom">
      {comments}
    </ReactTooltip>
  );
};

const BenefitRow = ({
  type,
  startDate,
  endDate,
  status,
  bookingReference,
  redamtionDate,
  reasonForCancellation,
  membershipNumber,
  comments,
  id
}) => (
  <StyledTableRow columns={14}>
    <StyledTableCell span={2}>{type}</StyledTableCell>
    <StyledTableCell span={2} justifyContent="center">
      {startDate}
    </StyledTableCell>
    <StyledTableCell span={2} justifyContent="center">
      {endDate}
    </StyledTableCell>
    <StyledTableCell span={2} justifyContent="center">
      {status}{' '}
      {comments !== '-' ? (
        <>
          {toolTipForComment(id, comments)}
          <AdditionalNotesIcon src={Information} data-for={id} data-tip />{' '}
        </>
      ) : null}
    </StyledTableCell>
    <StyledTableCell span={2} justifyContent="center">
      {bookingReference !== '-' ? (
        <BookingLink href={`/manage/bookings/${bookingReference}/journeys?membershipNumber=${membershipNumber}`} target="_blank">
          {bookingReference}
        </BookingLink>
      ) : (
        '-'
      )}
    </StyledTableCell>
    <StyledTableCell span={2} justifyContent="center">
      {redamtionDate}
    </StyledTableCell>
    <StyledTableCell span={2} justifyContent="center">
      {reasonForCancellation}
    </StyledTableCell>
  </StyledTableRow>
);

BenefitRow.propTypes = {
  type: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  status: PropTypes.string,
  bookingReference: PropTypes.string,
  redamtionDate: PropTypes.string,
  reasonForCancellation: PropTypes.string,
  membershipNumber: PropTypes.string,
  comments: PropTypes.string,
  id: PropTypes.string
};

const Benefits = ({ customerInfo }) => {
  const membershipNumber = customerInfo.profile?.loyalty?.membershipNumber;

  const {
    loading: memberBenefitsLoading,
    data: { loyaltyMemberBenefits = [] } = {},
    error: memberBenefitsError
  } = useQuery(GET_LOYALTY_MEMBER_BENEFITS, {
    variables: {
      membershipNumber
    },
    fetchPolicy: 'network-only'
  });

  const {
    loading: businessBenefitsLoading,
    data: businessBenefitsData,
    error: businessBenefitsError
  } = useQuery(GET_LOYALTY_BUSINESS_ACCOUNT_BENEFITS, {
    variables: {
      membershipNumber
    },
    fetchPolicy: 'network-only',
    skip: !membershipNumber
  });

  if (memberBenefitsError || businessBenefitsError) {
    throw new AgentPortalError(memberBenefitsError ?? businessBenefitsError);
  }

  if (memberBenefitsLoading || businessBenefitsLoading) {
    return (
      <SectionContainer>
        <Row span={16} height="10vh" data-testid="full-page-spinner">
          <Column justifyContent="center" alignItems="center">
            <Spinner />
          </Column>
        </Row>
      </SectionContainer>
    );
  }

  const businessBenefitsResponse = businessBenefitsData?.getLoyaltyBusinessAccountBenefits;
  const businessBenefits =
    businessBenefitsResponse && businessBenefitsResponse !== null
      ? businessBenefitsResponse.benefitList ?? businessBenefitsResponse.benefitLisr ?? []
      : [];
  const combinedBenefits = [...loyaltyMemberBenefits.toReversed(), ...businessBenefits.toReversed()];

  return (
    <SectionContainer>
      <SectionTableContainer data-testid="benefits-history-table" data-cy="benefits-history-table">
        <StyledTableRow columns={14}>
          <StyledTableHeader span={2} justifyContent="center" alignItems="center">
            <FormattedMessage id="clubEurostar.benefits.type" tagName="strong" />
          </StyledTableHeader>
          <StyledTableHeader span={2} justifyContent="center" alignItems="center">
            <FormattedMessage id="clubEurostar.benefits.startDate" tagName="strong" />
          </StyledTableHeader>
          <StyledTableHeader span={2} justifyContent="center" alignItems="center">
            <FormattedMessage id="clubEurostar.benefits.endDate" tagName="strong" />
          </StyledTableHeader>
          <StyledTableHeader span={2} justifyContent="center" alignItems="center">
            <FormattedMessage id="clubEurostar.benefits.status" tagName="strong" />
          </StyledTableHeader>
          <StyledTableHeader span={2} justifyContent="center" alignItems="center">
            <FormattedMessage id="clubEurostar.benefits.PNR" tagName="strong" />
          </StyledTableHeader>
          <StyledTableHeader span={2} justifyContent="center" alignItems="center">
            <FormattedMessage id="clubEurostar.benefits.redemptionDate" tagName="strong" />
          </StyledTableHeader>
          <StyledTableHeader span={2} justifyContent="center" alignItems="center">
            <FormattedMessage id="clubEurostar.benefits.reasonForCancellation" tagName="strong" />
          </StyledTableHeader>
        </StyledTableRow>
        {combinedBenefits.length === 0 ? (
          <StyledTableRow columns={13}>
            <StyledTableCell span={13} justifyContent="center">
              <FormattedMessage id="clubEurostar.notFound" />
            </StyledTableCell>
          </StyledTableRow>
        ) : (
          combinedBenefits.map((row) => (
            <BenefitRow
              key={`${row.id ?? row.benefitName}-${row.bookingReference ?? row.pnr ?? 'unknown'}`}
              type={row.benefitName ?? '-'}
              startDate={row.startDate ? moment(row.startDate).format(DATE_FORMAT) : '-'}
              endDate={row.endDate ? moment(row.endDate).format(DATE_FORMAT) : '-'}
              status={row.benefitStatus ?? '-'}
              bookingReference={row.bookingReference ?? row.pnr ?? '-'}
              redamtionDate={row.benefitRedemptionDate ? moment(row.benefitRedemptionDate).format(DATE_FORMAT) : ''}
              reasonForCancellation={row.cancellationReason ?? row.reasonForCancellation ?? '-'}
              membershipNumber={membershipNumber}
              comments={row.comments ?? '-'}
              id={row.id}
            />
          ))
        )}
      </SectionTableContainer>
    </SectionContainer>
  );
};

Benefits.propTypes = {
  customerInfo: customerInfoShape
};

export default Benefits;
